/**
 * AgentsRoom — Phase C Mission Control room.
 *
 * Renders the full agent fleet grid: Platform Pod + Legal Pod.
 * Features:
 *   - Live polling via useAgents (15 s interval)
 *   - "I AM DRIVING" override banner when any platform agent is paused with manual_override: reason
 *   - Dismissible degraded-agent warning chip
 *   - Loading skeleton (10 platform + 3 legal ghost tiles)
 *   - Error state with retry
 *   - AgentDrawer with pause / resume / kill wired to useAgents actions
 */
import { useState, useCallback } from "react";
import { Cpu, RefreshCw, AlertTriangle, Zap } from "lucide-react";
import { useAgents } from "../hooks/useAgents";
import AgentTile from "../agent/AgentTile";
import AgentDrawer from "../agent/AgentDrawer";

// ── Ghost tile for loading skeleton ──────────────────────────────────────────

function GhostTile() {
  return <div className="mc-agent-tile-ghost" aria-hidden="true" />;
}

// ── Section pod header ────────────────────────────────────────────────────────

function PodHeader({ label, count, right }) {
  return (
    <div className="mc-agents-pod-header" aria-label={`${label} section`}>
      <span className="mc-agents-pod-label">{label}</span>
      <span className="mc-agents-pod-count">{count} agent{count !== 1 ? "s" : ""}</span>
      {right}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AgentsRoom() {
  const {
    agents,
    platformAgents,
    legalAgents,
    total24hCost,
    loading,
    error,
    refetch,
    pauseAgent,
    resumeAgent,
    killAgent,
  } = useAgents();

  // Selected agent for drawer
  const [selectedAgent, setSelectedAgent] = useState(null);

  // Degraded chip dismissal — local session only
  const [degradedDismissed, setDegradedDismissed] = useState(false);

  // ── Derived state ───────────────────────────────────────────────────────────

  // Override is active when ANY platform agent is paused with a manual_override: reason
  const isManualOverrideActive = platformAgents.some(
    (a) =>
      a.status === "paused" &&
      typeof a.paused_reason === "string" &&
      a.paused_reason.startsWith("manual_override:"),
  );

  // All agents with status_computed === "degraded"
  const degradedAgents = agents.filter(
    (a) => (a.status_computed || a.status) === "degraded",
  );
  const hasDegraded = degradedAgents.length > 0 && !degradedDismissed;

  // Legal pod average escalation rate (0–100 %)
  const legalWithRate = legalAgents.filter((a) => a.escalation_rate != null);
  const avgEscalationPct =
    legalWithRate.length > 0
      ? (
          (legalWithRate.reduce((sum, a) => sum + a.escalation_rate, 0) /
            legalWithRate.length) *
          100
        ).toFixed(1)
      : null;

  // ── Subtitle line ───────────────────────────────────────────────────────────

  const subtitleParts = [];
  subtitleParts.push(`${agents.length} agent${agents.length !== 1 ? "s" : ""}`);
  if (total24hCost != null) {
    subtitleParts.push(`$${Number(total24hCost).toFixed(2)} / 24h`);
  }
  if (degradedAgents.length > 0) {
    subtitleParts.push(`${degradedAgents.length} degraded`);
  }
  const subtitle = subtitleParts.join(" · ");

  // ── Drawer action callbacks ─────────────────────────────────────────────────

  const handlePause = useCallback(
    (reason) => pauseAgent(selectedAgent.agent_id, reason),
    [pauseAgent, selectedAgent],
  );

  const handleResume = useCallback(
    () => resumeAgent(selectedAgent.agent_id),
    [resumeAgent, selectedAgent],
  );

  const handleKill = useCallback(
    (reason) => killAgent(selectedAgent.agent_id, reason),
    [killAgent, selectedAgent],
  );

  // ── Error state ─────────────────────────────────────────────────────────────

  if (error && agents.length === 0) {
    return (
      <section className="mc-room mc-room--agents" aria-label="Agents room">
        <div className="mc-room-eyebrow">ROOM · AGENTS</div>
        <h1 className="mc-room-title">Agents</h1>
        <div
          style={{
            marginTop: 32,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 14,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 14,
              fontFamily: "var(--font-sans)",
              color: "var(--color-critical)",
            }}
          >
            Could not load agent registry. Check API connectivity.
          </p>
          <button
            onClick={refetch}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              fontSize: 12,
              fontFamily: "var(--font-sans)",
              border: "1px solid var(--mc-border)",
              borderRadius: 4,
              background: "none",
              color: "var(--mc-text)",
              cursor: "pointer",
            }}
          >
            <RefreshCw size={13} />
            Retry
          </button>
        </div>
      </section>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <section className="mc-room mc-room--agents" aria-label="Agents room">

      {/* ── Room header ──────────────────────────────────────────────────── */}
      <div className="mc-room-header">
        <div>
          <p className="mc-room-eyebrow">ROOM · AGENTS</p>
          <h1 className="mc-room-title">
            Agents
          </h1>
          {!loading && agents.length > 0 && (
            <p
              className="mc-room-meta"
              style={{ marginTop: 4 }}
              aria-label="Agent fleet summary"
            >
              {subtitle}
            </p>
          )}
        </div>

        <button
          onClick={refetch}
          aria-label="Refresh agents"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            fontSize: 12,
            fontFamily: "var(--font-sans)",
            border: "1px solid var(--mc-border)",
            borderRadius: 4,
            background: "none",
            color: "var(--color-slate-400)",
            cursor: "pointer",
            transition: "color 150ms, border-color 150ms",
            flexShrink: 0,
            alignSelf: "flex-start",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--mc-text)";
            e.currentTarget.style.borderColor = "var(--mc-text)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--color-slate-400)";
            e.currentTarget.style.borderColor = "var(--mc-border)";
          }}
        >
          <RefreshCw size={13} />
          refresh
        </button>
      </div>

      {/* ── "I AM DRIVING" override banner ───────────────────────────────── */}
      {isManualOverrideActive && (
        <div
          className="mc-agents-override-banner"
          role="alert"
          aria-live="polite"
        >
          <AlertTriangle size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
          <span>
            I AM DRIVING — all platform agents are paused. Legal pod operating normally.
          </span>
        </div>
      )}

      {/* ── Degraded chip ─────────────────────────────────────────────────── */}
      {hasDegraded && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 10px 4px 8px",
            border: "1px solid rgba(224, 122, 78, 0.35)",
            borderRadius: 4,
            background: "rgba(224, 122, 78, 0.10)",
            color: "#C45E28",
            fontSize: 12,
            fontFamily: "var(--font-sans)",
            marginBottom: 16,
          }}
          role="status"
          aria-live="polite"
        >
          <AlertTriangle size={13} aria-hidden="true" style={{ flexShrink: 0 }} />
          <span>
            {degradedAgents.length} agent{degradedAgents.length !== 1 ? "s" : ""} degraded — check drawer for details
          </span>
          <button
            onClick={() => setDegradedDismissed(true)}
            aria-label="Dismiss degraded warning"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#C45E28",
              padding: "0 2px",
              fontSize: 14,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Loading skeleton ──────────────────────────────────────────────── */}
      {loading ? (
        <>
          {/* Platform pod skeleton */}
          <div className="mc-agents-pod-header">
            <span className="mc-agents-pod-label">PLATFORM POD</span>
            <span
              className="mc-agents-pod-count"
              style={{ opacity: 0.3 }}
            >
              — agents
            </span>
          </div>
          <div className="mc-agents-skeleton" aria-label="Loading platform agents" aria-busy="true">
            {Array.from({ length: 10 }, (_, i) => (
              <GhostTile key={`ghost-platform-${i}`} />
            ))}
          </div>

          {/* Legal pod skeleton */}
          <div className="mc-agents-pod-header" style={{ marginTop: 28 }}>
            <span className="mc-agents-pod-label">LEGAL POD</span>
            <span
              className="mc-agents-pod-count"
              style={{ opacity: 0.3 }}
            >
              — agents
            </span>
          </div>
          <div className="mc-agents-skeleton" aria-label="Loading legal agents" aria-busy="true">
            {Array.from({ length: 3 }, (_, i) => (
              <GhostTile key={`ghost-legal-${i}`} />
            ))}
          </div>
        </>
      ) : (
        <>
          {/* ── Platform Pod ─────────────────────────────────────────────── */}
          <PodHeader
            label="PLATFORM POD"
            count={platformAgents.length}
            right={
              total24hCost != null ? (
                <span className="mc-agents-pod-cost">
                  ${Number(total24hCost).toFixed(2)} / 24h
                </span>
              ) : null
            }
          />

          {platformAgents.length === 0 ? (
            <p
              style={{
                fontSize: 13,
                color: "var(--color-slate-500)",
                fontFamily: "var(--font-sans)",
                margin: "8px 0 0",
              }}
            >
              No platform agents registered.
            </p>
          ) : (
            <div className="mc-agents-grid" role="list" aria-label="Platform agents">
              {platformAgents.map((agent) => (
                <div key={agent.agent_id} role="listitem">
                  <AgentTile
                    agent={agent}
                    onSelect={setSelectedAgent}
                    isOverride={isManualOverrideActive}
                  />
                </div>
              ))}
            </div>
          )}

          {/* ── Legal Pod ────────────────────────────────────────────────── */}
          <PodHeader
            label="LEGAL POD"
            count={legalAgents.length}
            right={
              avgEscalationPct != null ? (
                <span className="mc-agents-pod-escalation">
                  avg {avgEscalationPct}% escalation
                </span>
              ) : null
            }
          />

          {legalAgents.length === 0 ? (
            <p
              style={{
                fontSize: 13,
                color: "var(--color-slate-500)",
                fontFamily: "var(--font-sans)",
                margin: "8px 0 0",
              }}
            >
              No legal agents registered.
            </p>
          ) : (
            <div className="mc-agents-grid" role="list" aria-label="Legal agents">
              {legalAgents.map((agent) => (
                <div key={agent.agent_id} role="listitem">
                  <AgentTile
                    agent={agent}
                    onSelect={setSelectedAgent}
                    isOverride={false}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Agent Drawer (renders null when selectedAgent is null) ────────── */}
      <AgentDrawer
        agent={selectedAgent}
        onClose={() => setSelectedAgent(null)}
        onPause={handlePause}
        onResume={handleResume}
        onKill={handleKill}
      />
    </section>
  );
}
